import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Container, Row, Col, Card, CardBody, CardTitle, CardText, Form, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchAllPhotos } from "../utils/apiService";
import { deleteReceipt } from "../utils/apiService";
import { photoUpload } from "../utils/apiService";
import { getImage } from "../utils/apiService";

const Upload = ({ uploadTypeOverride, hideStyleExamples }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [newUploadedFiles, setNewUploadedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDropzoneEnabled, setIsDropzoneEnabled] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setNewUploadedFiles([file]);
      setIsUploading(true);
      setUploadError(null);
      
      try {
        // Get access token for authentication
        let token = null;
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          console.log('No access token available for upload:', e);
        }
        
        // Call photoUpload with the selected file and token
        const result = await photoUpload(file, token);
        console.log('Photo upload successful:', result);
        
        // Reload photos after successful upload
        const loadPhotos = async () => {
          try {
            let token = null;
            try {
              token = await getAccessTokenSilently();
            } catch (e) {
              console.log('No access token available:', e);
            }
            
            const photos = await fetchAllPhotos(token);
            console.log('Reloaded photos after upload:', photos);
            
            // Extract fileList from the API response structure
            if (photos && photos.uploadConfigs && photos.uploadConfigs.length > 0) {
              const fileList = photos.uploadConfigs[0].fileList || [];
              setExistingFiles(fileList);
              
              // Load images for each file
              if (token) {
                fileList.forEach(file => {
                  loadImage(file.fileId, token);
                });
              }
            } else {
              setExistingFiles([]);
            }
          } catch (error) {
            console.error('Error reloading photos after upload:', error);
          }
        };
        
        await loadPhotos();
      } catch (error) {
        console.error('Photo upload failed:', error);
        setUploadError(error.message);
      } finally {
        setIsUploading(false);
      }
    }
  }, [getAccessTokenSilently]);

  // Function to load individual image
  const loadImage = async (fileId, accessToken) => {
    try {
      setImageLoadingStates(prev => ({ ...prev, [fileId]: true }));
      const imageBlob = await getImage(fileId, accessToken);
      const imageUrl = URL.createObjectURL(imageBlob);
      setImageUrls(prev => ({ ...prev, [fileId]: imageUrl }));
    } catch (error) {
      console.error(`Error loading image ${fileId}:`, error);
    } finally {
      setImageLoadingStates(prev => ({ ...prev, [fileId]: false }));
    }
  };

  // Load photos on component mount
  useEffect(() => {
    const loadPhotos = async () => {
      try {
        let token = null;
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          console.log('No access token available:', e);
        }
        
        const photos = await fetchAllPhotos(token);
        console.log('Loaded photos:', photos);
        
        // Extract fileList from the API response structure
        if (photos && photos.uploadConfigs && photos.uploadConfigs.length > 0) {
          const fileList = photos.uploadConfigs[0].fileList || [];
          setExistingFiles(fileList);
          
          // Load images for each file
          if (token) {
            fileList.forEach(file => {
              loadImage(file.fileId, token);
            });
          }
        } else {
          setExistingFiles([]);
        }
      } catch (error) {
        console.error('Error loading photos:', error);
        setExistingFiles([]);
      }
    };

    loadPhotos();
  }, [getAccessTokenSilently]);

  // Cleanup effect to revoke object URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  // Enable dropzone when there are no files or when files are deleted
  useEffect(() => {
    if (existingFiles.length === 0) {
      setIsDropzoneEnabled(true);
    }
  }, [existingFiles.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    multiple: false,
    disabled: !isDropzoneEnabled || existingFiles.length > 0
  });

  const choices = [
    // {
    //   id: 1,
    //   title: "ChatGPT",
    //   description: "Setup Login",
    //   icon: "/Chat-GPT-Logo.png"
    // },
    {
      id: 2,
      title: "Linkedin", 
      description: "Setup Login",
      icon: "/LinkedIn-Logo.png"
    },
    {
      id: 3,
      title: "Facebook Ads",
      description: "Retrieve Now",
      icon: "/facebook-ads-beginners-guide-ban.png"
    },
    // {
    //   id: 4,
    //   title: "Hubspot",
    //   description: "Setup Login",
    //   icon: "/HubSpot-Logo.wine.png"
    // },
    {
      id: 5,
      title: "Salesforce",
      description: "Setup Login",
      icon: "/Salesforce.com-Logo.wine.png"
    },
    // {
    //   id: 6,
    //   title: "AWS",
    //   description: "Setup Login",
    //   icon: "/Amazon_Web_Services-Logo.wine.png"
    // }
  ];

  const handleChoiceSelect = (choiceId) => {
    // if (choiceId === 1 || choiceId === 3 || choiceId === 4 || choiceId === 5 || choiceId === 6) {
    if (choiceId === 3 || choiceId === 5) {
      setShowComingSoonModal(true);
    } else {
      setSelectedChoice(choiceId);
    }
  };

  const handlePhotoDelete = async (fileId) => {
    try {
      const accessToken = await getAccessTokenSilently();
      await deleteReceipt(fileId, accessToken);
      console.log(`Photo ${fileId} deleted successfully`);
      
      // Remove the deleted file from the existing files list
      setExistingFiles(prevFiles => prevFiles.filter(file => file.fileId !== fileId));
      
      // Clean up the image URL and loading state
      setImageUrls(prev => {
        const newUrls = { ...prev };
        if (newUrls[fileId]) {
          URL.revokeObjectURL(newUrls[fileId]);
          delete newUrls[fileId];
        }
        return newUrls;
      });
      setImageLoadingStates(prev => {
        const newStates = { ...prev };
        delete newStates[fileId];
        return newStates;
      });
    } catch (error) {
      console.error('Error deleting photo:', error);
      // TODO: Show error message to user
    }
  };

  const renderQuestions = () => {
    if (!selectedChoice) return null;

    switch (selectedChoice) {
      // case 1: // ChatGPT
      //   return (
      //     <Card className="mt-4">
      //       <CardBody>
      //         <CardTitle tag="h4" className="mb-3">ChatGPT Configuration</CardTitle>
      //         <Form>
      //           <Row>
      //             <Col md={6}>
      //               <FormGroup>
      //                 <Label for="height">Height (cm)</Label>
      //                 <Input type="number" id="height" placeholder="Enter your height" />
      //               </FormGroup>
      //             </Col>
      //             <Col md={6}>
      //               <FormGroup>
      //                 <Label for="weight">Weight (kg)</Label>
      //                 <Input type="number" id="weight" placeholder="Enter your weight" />
      //               </FormGroup>
      //             </Col>
      //           </Row>
      //           <FormGroup>
      //             <Label for="bodyType">Body Type</Label>
      //             <Input type="select" id="bodyType">
      //               <option>Select body type</option>
      //               <option>Slim</option>
      //               <option>Athletic</option>
      //               <option>Average</option>
      //               <option>Curvy</option>
      //             </Input>
      //           </FormGroup>
      //           <FormGroup>
      //             <Label for="selfieUpload">Upload Selfie</Label>
      //             <Input type="file" id="selfieUpload" accept="image/*" />
      //           </FormGroup>
      //           <Button color="primary" className="mt-3">Continue</Button>
      //         </Form>
      //       </CardBody>
      //     </Card>
      //   );

      case 2: // Linkedin
        return null;

      case 3: // Facebook Ads
        return (
          <Card className="mt-4">
            <CardBody>
              <CardTitle tag="h4" className="mb-3">Facebook Ads Configuration</CardTitle>
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="chest">Chest (cm)</Label>
                      <Input type="number" id="chest" placeholder="Chest measurement" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="waist">Waist (cm)</Label>
                      <Input type="number" id="waist" placeholder="Waist measurement" />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="hips">Hips (cm)</Label>
                      <Input type="number" id="hips" placeholder="Hips measurement" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="inseam">Inseam (cm)</Label>
                      <Input type="number" id="inseam" placeholder="Inseam measurement" />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="bodyType">Body Type</Label>
                  <Input type="select" id="bodyType">
                    <option>Select body type</option>
                    <option>Slim</option>
                    <option>Athletic</option>
                    <option>Average</option>
                    <option>Curvy</option>
                  </Input>
                </FormGroup>
                <Button color="primary" className="mt-3">Continue</Button>
              </Form>
            </CardBody>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ position: 'relative' }}>
      <Container>
      <Row className="justify-content-center">
        <Col md={10}>
          <div className="text-center mb-4">
            <h2 className="mb-3">Select supplier</h2>
            <p className="text-muted">Choose the option that best fits your needs</p>
          </div>
          
          <Row className="justify-content-center">
            {choices.map((choice) => (
              <Col key={choice.id} xs={4} sm={6} md={4} className="mb-3">
                <Card 
                  className={`choice-card ${selectedChoice === choice.id ? 'selected' : ''}`}
                >
                  <CardBody className="text-center">
                    <div className="choice-icon mb-2" style={{ height: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}>
                      {choice.icon.startsWith('/') ? (
                        <img src={choice.icon} alt={choice.title} style={{ width: '4.5rem', height: '4.5rem', objectFit: 'contain' }} />
                      ) : (
                        <span style={{ fontSize: '1.5rem' }}>{choice.icon}</span>
                      )}
                    </div>
                    <CardTitle tag="h6" className="mb-1">
                      {choice.title}
                    </CardTitle>
                    {choice.description === "Setup Login" && (
                      <div className="text-center mt-2 d-none d-sm-block">
                        <Button 
                          color="primary" 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChoiceSelect(choice.id);
                          }}
                        >
                          Setup Login
                        </Button>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Mobile-only Dynamic Caption Text */}
          <div className="text-center mt-3 mb-4 d-block d-sm-none">
            <p className="text-muted small">
              {/* {selectedChoice === 1 && "Upload a selfie and provide body measurements for the most accurate fit recommendations."} */}
              {selectedChoice === 2 && "Connect your LinkedIn account to automatically retrieve receipts."}
              {selectedChoice === 3 && "Enter your body measurements manually for personalized clothing recommendations."}
            </p>
          </div>

                        {/* Dynamic Questions Section */}
              <div style={{ position: 'relative' }}>
                {renderQuestions()}
              </div>
        </Col>
      </Row>
    </Container>
    
    {/* Right Side Menu for LinkedIn */}
    {selectedChoice === 2 && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '400px',
          height: '100vh',
          backgroundColor: '#fff',
          boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
          zIndex: 1050,
          overflowY: 'auto',
          transition: 'transform 0.3s ease-in-out'
        }}
      >
        <div style={{ padding: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <h4>LinkedIn Setup</h4>
            <Button
              color="link"
              onClick={() => setSelectedChoice(null)}
              style={{ padding: 0, fontSize: '1.5rem', lineHeight: 1 }}
            >
              ×
            </Button>
          </div>
          <Form>
            <FormGroup>
              <Label for="linkedinEmail">LinkedIn Email</Label>
              <Input type="email" id="linkedinEmail" placeholder="Enter your LinkedIn email" />
            </FormGroup>
            <FormGroup>
              <Label for="linkedinPassword">Password</Label>
              <Input type="password" id="linkedinPassword" placeholder="Enter your password" />
            </FormGroup>
            <Button color="primary" className="mt-3" block>Connect LinkedIn</Button>
          </Form>
        </div>
      </div>
    )}
    
    {/* Backdrop when LinkedIn menu is open */}
    {selectedChoice === 2 && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={() => setSelectedChoice(null)}
      />
    )}
    </div>
    
    {/* Coming Soon Modal */}
    <Modal isOpen={showComingSoonModal} toggle={() => setShowComingSoonModal(false)} centered>
      <ModalHeader toggle={() => setShowComingSoonModal(false)}>
        🚀 Coming Soon
      </ModalHeader>
      <ModalBody>
        <div className="text-center">
          <div className="mb-3">
            <span style={{ fontSize: '3rem' }}>⏳</span>
          </div>
          <h5 className="mb-3">These functions will be released shortly</h5>
          <p className="text-muted">
            We're working hard to bring you these exciting new features. 
            Stay tuned for updates!
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={() => setShowComingSoonModal(false)}>
          Got it!
        </Button>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default Upload;
